import { ChevronDown, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import configService from '../../services/configService';

interface Category {
  _id: string;
  value: string;
  label: string;
  sortOrder?: number;
  metadata?: {
    color?: string;
    description?: string;
  };
}

interface CategorySelectorProps {
  value?: string;
  onChange: (categoryId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  label?: string;
}

const ImprovedCategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a category...',
  required = false,
  label = 'Meeting Category',
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Default fallback categories
  const defaultCategories: Category[] = [
    { _id: 'default-1', value: 'planning', label: 'Planning Meeting', sortOrder: 1 },
    { _id: 'default-2', value: 'review', label: 'Review Meeting', sortOrder: 2 },
    { _id: 'default-3', value: 'standup', label: 'Daily Standup', sortOrder: 3 },
    { _id: 'default-4', value: 'retrospective', label: 'Retrospective', sortOrder: 4 },
    { _id: 'default-5', value: 'client', label: 'Client Meeting', sortOrder: 5 },
    { _id: 'default-6', value: 'team', label: 'Team Meeting', sortOrder: 6 },
    { _id: 'default-7', value: 'other', label: 'Other', sortOrder: 99 },
  ];

  // Load categories from configuration with fallback
  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      setHasError(false);

      try {
        // getConfiguration returns TaskDropdownOption[] directly
        const options = await configService.getConfiguration('meeting-types');

        if (options && options.length > 0) {
          const formattedCategories = options.map((option: any, index: number) => ({
            _id: option._id || `config-${index}`,
            value: option.value || option.label?.toLowerCase().replace(/\s+/g, '_'),
            label: option.label || option.value,
            sortOrder: option.sortOrder || option.order || index,
            metadata: option.metadata,
          }));

          setCategories(formattedCategories);
        } else {
          console.warn('No meeting types configuration found, using defaults');
          setCategories(defaultCategories);
        }
      } catch (err) {
        console.error('Failed to load meeting categories:', err);
        setHasError(true);
        setCategories(defaultCategories);
        // Don't show error toast for this, as we have fallbacks
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  const selectedCategory = categories.find(cat => cat._id === value || cat.value === value);

  const clearSelection = () => {
    onChange('');
    setIsOpen(false);
  };

  const selectCategory = (category: Category) => {
    onChange(category._id);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.category-selector')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="category-selector">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
        {hasError && (
          <span className="text-orange-500 text-xs ml-2">
            (Using defaults due to connection issue)
          </span>
        )}
      </label>

      {/* Dropdown trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={`w-full h-10 flex items-center justify-between px-3 py-2 text-left bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors ${
            disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'
          }`}
        >
          <div className="flex items-center flex-1 min-w-0">
            {loading ? (
              <span className="text-gray-500 text-sm">Loading categories...</span>
            ) : selectedCategory ? (
              <div className="flex items-center space-x-2">
                {selectedCategory.metadata?.color && (
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedCategory.metadata.color }}
                  />
                )}
                <span className="text-gray-900 truncate text-sm">{selectedCategory.label}</span>
              </div>
            ) : (
              <span className="text-gray-500 text-sm">{placeholder}</span>
            )}
          </div>

          <div className="flex items-center space-x-1 ml-2">
            {selectedCategory && !disabled && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  clearSelection();
                }}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {/* Dropdown menu */}
        {isOpen && !disabled && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg border border-gray-200 overflow-auto">
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-500">Loading categories...</div>
            ) : categories.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">No categories available</div>
            ) : (
              <>
                {categories
                  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                  .map(category => (
                    <button
                      key={category._id}
                      type="button"
                      onClick={() => selectCategory(category)}
                      className={`w-full px-4 py-3 text-left hover:bg-brand-50 transition-colors ${
                        selectedCategory?._id === category._id ||
                        selectedCategory?.value === category.value
                          ? 'bg-brand-100 text-brand-900'
                          : 'text-gray-900'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        {category.metadata?.color && (
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: category.metadata.color }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{category.label}</div>
                          {category.metadata?.description && (
                            <div className="text-xs text-gray-500 mt-1">
                              {category.metadata.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
              </>
            )}
          </div>
        )}
      </div>

      {hasError && (
        <p className="mt-1 text-xs text-orange-600">
          Using default categories. Some options may not be available.
        </p>
      )}
    </div>
  );
};

export default ImprovedCategorySelector;
